/**
 * « Changer mon mot de passe » — depuis l'en-tête, pour tout le personnel.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI CET ÉCRAN EXISTE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le produit savait POSER un mot de passe — à la création d'un compte, et
 * depuis peu à la demande d'un administrateur — et ne savait pas en CHANGER.
 * Concrètement : un guichetier gardait à vie celui que son administratrice
 * avait tapé pour lui, et qu'elle connaît. Pendant ce temps, la modale
 * d'environnement de test lui demande de « ne jamais utiliser un mot de passe
 * employé ailleurs » : la consigne existait, le moyen non.
 *
 * Il vit dans l'en-tête, à côté du nom, parce que c'est le seul endroit commun
 * aux trois rôles — la configuration est réservée à l'administration, et c'est
 * précisément le guichet qui en a le plus besoin.
 *
 * ★ **Les autres sessions tombent, celle-ci reste.** C'est écrit à l'écran
 *   avant le geste, pas découvert après : le poste resté ouvert au fond du
 *   hall est souvent la raison même du changement.
 */

import { useState } from 'react'

import { messageErreur } from '../lib/api'
import { Button } from '../ui/Button'
import { FormField } from '../ui/FormField'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { useToast } from '../ui/Toast'
import { useChangerMonMotDePasse } from '../auth'

const VIDE = { actuel: '', nouveau: '', confirmation: '' }

export function MonMotDePasse({
  ouvert,
  onFermer,
}: {
  readonly ouvert: boolean
  readonly onFermer: () => void
}) {
  const [saisie, setSaisie] = useState(VIDE)
  const [erreur, setErreur] = useState<string | null>(null)
  const changer = useChangerMonMotDePasse()
  const toast = useToast()

  function fermer(): void {
    setSaisie(VIDE)
    setErreur(null)
    onFermer()
  }

  function soumettre(): void {
    if (saisie.actuel === '') {
      setErreur('Saisissez votre mot de passe actuel.')
      return
    }
    if (saisie.nouveau.length < 8) {
      setErreur('Le nouveau mot de passe fait 8 caractères au minimum.')
      return
    }
    /* La confirmation est vérifiée ICI et pas au serveur : c'est une faute de
       frappe, pas une règle métier, et l'aller-retour n'apprendrait rien. */
    if (saisie.nouveau !== saisie.confirmation) {
      setErreur('Les deux nouveaux mots de passe ne correspondent pas.')
      return
    }
    setErreur(null)
    changer.mutate(
      { actuel: saisie.actuel, nouveau: saisie.nouveau },
      {
        onSuccess: () => {
          toast.succes('Mot de passe changé. Vos autres sessions ont été fermées.')
          fermer()
        },
        onError: (cause) => {
          setErreur(messageErreur(cause))
        },
      },
    )
  }

  return (
    <Modal
      ouvert={ouvert}
      onFermer={fermer}
      titre="Changer mon mot de passe"
      taille="sm"
      pied={
        <>
          <Button variante="secondaire" onClick={fermer}>
            Annuler
          </Button>
          <Button chargement={changer.isPending} onClick={soumettre}>
            Changer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Vos autres sessions ouvertes seront fermées — un poste resté connecté ailleurs devra se
          reconnecter. Celle-ci reste valable.
        </p>

        <FormField label="Mot de passe actuel" requis>
          {(champ) => (
            <Input
              {...champ}
              type="password"
              autoComplete="current-password"
              value={saisie.actuel}
              onChange={(e) => {
                setSaisie({ ...saisie, actuel: e.target.value })
              }}
            />
          )}
        </FormField>

        <FormField label="Nouveau mot de passe" requis aide="8 caractères au minimum.">
          {(champ) => (
            <Input
              {...champ}
              type="password"
              autoComplete="new-password"
              value={saisie.nouveau}
              onChange={(e) => {
                setSaisie({ ...saisie, nouveau: e.target.value })
              }}
            />
          )}
        </FormField>

        <FormField label="Répéter le nouveau mot de passe" requis erreur={erreur ?? undefined}>
          {(champ) => (
            <Input
              {...champ}
              type="password"
              autoComplete="new-password"
              invalide={erreur !== null}
              value={saisie.confirmation}
              onChange={(e) => {
                setSaisie({ ...saisie, confirmation: e.target.value })
              }}
            />
          )}
        </FormField>
      </div>
    </Modal>
  )
}
